/**
 * SG PreSet — Change of Registered Office Address (B32105)
 * Directors' Resolution (Change of Registered Office Address)
 *
 * Template variables: company_name, company_registration_no, new_office_address, dated
 * E-Filing payload: officeAddr { postalCode, unitNo, levelNo, blkhseNo, streetName, buildingName, haveLevelUnit, addressType }, addressEffectiveDate
 */

export interface OfficeAddrModel {
  postalCode: string;
  blkHseNo: string;
  streetName: string;
  buildingName: string;
  levelNo: string;
  unitNo: string;
  haveLevelUnit: boolean;
}

export interface ChangeAddressFormModel {
  companyName: string;
  companyRegistrationNo: string;
  officeAddr: OfficeAddrModel;
  dated: string;
}

export function createEmptyOfficeAddr(): OfficeAddrModel {
  return {
    postalCode: '',
    blkHseNo: '',
    streetName: '',
    buildingName: '',
    levelNo: '',
    unitNo: '',
    haveLevelUnit: true,
  };
}

export function createEmptyChangeAddressForm(): ChangeAddressFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    officeAddr: createEmptyOfficeAddr(),
    dated: '',
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** 주소를 한 줄 문자열로 조합 — backend buildRegisterAddress() (document.js) 패턴과 동일 */
export function formatOfficeAddressOneLine(addr: OfficeAddrModel): string {
  const blockNo = trim(addr.blkHseNo);
  const streetName = trim(addr.streetName);
  const addrLevel = trim(addr.levelNo);
  const addrUnit = trim(addr.unitNo);
  const buildingName = trim(addr.buildingName);
  const postalCode = trim(addr.postalCode);

  let address = blockNo;
  if (streetName) address += (address ? ' ' : '') + streetName;
  if (addrLevel) address += (address ? ' #' : '#') + addrLevel;
  if (addrUnit) address += (address ? '-' : '') + addrUnit;

  if (buildingName || postalCode) {
    address += ' ';
    if (buildingName) {
      address += buildingName;
      if (postalCode) address += ' ';
    }
    if (postalCode) {
      address += 'Singapore ' + postalCode;
    }
  }

  return address.trim();
}

export function toChangeAddressTemplateVariables(
  model: ChangeAddressFormModel,
): Record<string, any> {
  return {
    company_name: trim(model.companyName),
    company_registration_no: trim(model.companyRegistrationNo),
    new_office_address: formatOfficeAddressOneLine(model.officeAddr),
    dated: trim(model.dated),
    signers: '',
    Signers: '',
  };
}

export function isChangeAddressFormComplete(model: ChangeAddressFormModel): boolean {
  const addr = model.officeAddr;
  return [
    model.companyName,
    model.companyRegistrationNo,
    addr.postalCode,
    addr.streetName,
    model.dated,
  ].every((v) => trim(v).length > 0);
}

/** E-Filing API 용 SaveDraftOfficeAddress payload */
export function buildChangeAddressEfilingPayload(
  model: ChangeAddressFormModel,
  entityUen: string,
  filingAgentUen: string,
) {
  const addr = model.officeAddr;
  const saveOfficeAddress = {
    OfficeAddr: {
      PostalCode: trim(addr.postalCode),
      UnitNo: trim(addr.unitNo),
      LevelNo: trim(addr.levelNo),
      BlkHseNo: trim(addr.blkHseNo),
      StreetName: trim(addr.streetName),
      BuildingName: trim(addr.buildingName),
      HaveLevelUnit: addr.haveLevelUnit,
      AddressType: '',
    },
    AddressEffectiveDate: trim(model.dated),
    OfficeAddrFc: {},
    AddressEffectiveDateFc: '',
  };
  return {
    eFilingDataForApi: {
      AddOfficeAddressList: [saveOfficeAddress],
      FilingAgentUEN: filingAgentUen,
      EntityUEN: entityUen,
    },
  };
}

/**
 * SG 파이썬 데몬(BM02C) 용 officeAddressList — C# SaveOfficeAddress JSON 키(camelCase) 그대로.
 * POST /tilkoapi/bizfile/office-address/save-draft 의 officeAddressList 로 전달.
 */
export function buildChangeAddressDaemonOfficeAddressList(model: ChangeAddressFormModel) {
  const addr = model.officeAddr;
  return [
    {
      officeAddr: {
        postalCode: trim(addr.postalCode),
        unitNo: trim(addr.unitNo),
        levelNo: trim(addr.levelNo),
        blkhseNo: trim(addr.blkHseNo),
        streetName: trim(addr.streetName),
        buildingName: trim(addr.buildingName),
        haveLevelUnit: addr.haveLevelUnit,
        // 등록사무소 = 싱가포르 국내 주소 → 'S'(Local). 웹 직접 진행 패킷 기준.
        //   빈 값이면 BizFile 화면에서 draft 를 열어 Review and confirm 해도 다음 단계로 안 넘어간다.
        addressType: 'S',
      },
      addressEffectiveDate: trim(model.dated),
      officeAddrFc: {},
      addressEffectiveDateFc: '',
    },
  ];
}

export function buildChangeAddressTaskFormData(
  model: ChangeAddressFormModel,
  entityCd: string,
): Record<string, any> {
  return {
    entity: trim(entityCd),
    entityCd: trim(entityCd),
    entityName: { value: trim(model.companyName) },
    entityRegNo: { value: trim(model.companyRegistrationNo) },
    companyName: trim(model.companyName),
    companyRegistrationNo: trim(model.companyRegistrationNo),
    newOfficeAddress: formatOfficeAddressOneLine(model.officeAddr),
    officeAddr: { ...model.officeAddr },
    dated: trim(model.dated),
  };
}
